import { RenderClearFlag } from "../../../RenderEngine/RenderEnum/RenderClearFlag";
import { CommandBuffer } from "../../../d3/core/render/command/CommandBuffer";
import { DepthPass } from "../../../d3/depthMap/DepthPass";
import { CameraCullInfo } from "../../../d3/shadowMap/ShadowSliceData";
import { LayaGL } from "../../../layagl/LayaGL";
import { StatElement } from "../../../layagl/StatisticsContext";
import { Color } from "../../../maths/Color";
import { Vector4 } from "../../../maths/Vector4";
import { Viewport } from "../../../maths/Viewport";
import { DepthTextureMode } from "../../../resource/RenderTexture";
import { Browser } from "../../../utils/Browser";
import { RenderCullUtil } from "../../DriverCommon/RenderCullUtil";
import { RenderListQueue } from "../../DriverCommon/RenderListQueue";
import { WebGLRenderContext3D } from "./WebGLRenderContext3D";
export class WebGLForwardAddClusterRP {
    get enableOpaqueTexture() {
        return this._enableOpaqueTexture;
    }
    set enableOpaqueTexture(value) {
        this._enableOpaqueTexture = value;
    }
    setViewPort(value) {
        value.cloneTo(this._viewPort);
    }
    ;
    setScissor(value) {
        value.cloneTo(this._scissor);
    }
    constructor() {
        this.blitOpaqueBuffer = new CommandBuffer();
        this.opaqueList = new RenderListQueue(false);
        this.transparent = new RenderListQueue(true);
        this.cameraCullInfo = new CameraCullInfo();
        this._zBufferParams = new Vector4();
        this._scissor = new Vector4();
        this._viewPort = new Viewport();
        this._defaultNormalDepthColor = new Color(0.5, 0.5, 1.0, 0.0);
        this.clearColor = new Color();
        this.depthPipelineMode = "ShadowCaster";
        this.depthNormalPipelineMode = "DepthNormal";
        let context = WebGLRenderContext3D._instance;
        context._preDrawUniformMaps.add("Scene3D");
        context._preDrawUniformMaps.add("Shadow");
        context._preDrawUniformMaps.add("Global");
    }
    setCameraCullInfo(value) {
        this.cameraCullInfo.position = value._transform.position;
        this.cameraCullInfo.cullingMask = value.cullingMask;
        this.cameraCullInfo.staticMask = value.staticMask;
        this.cameraCullInfo.boundFrustum = value.boundFrustum;
        this.cameraCullInfo.useOcclusionCulling = value.useOcclusionCulling;
    }
    setBeforeForwardCmds(value) {
        if (value && value.length > 0) {
            this.beforeForwardCmds = value;
            value.forEach(element => {
                element._apply(false);
            });
        }
    }
    setBeforeSkyboxCmds(value) {
        if (value && value.length > 0) {
            this.beforeSkyboxCmds = value;
            value.forEach(element => {
                element._apply(false);
            });
        }
    }
    setBeforeTransparentCmds(value) {
        if (value && value.length > 0) {
            this.beforeTransparentCmds = value;
            value.forEach(element => {
                element._apply(false);
            });
        }
    }
    render(context, list, count) {
        context.cameraUpdateMask++;
        this.opaqueList.clear();
        this.transparent.clear();
        var time = Browser.now();
        RenderCullUtil.cullByCameraCullInfo(this.cameraCullInfo, list, count, this.opaqueList, this.transparent, context);
        LayaGL.statAgent.recordTimeData(StatElement.T_CullMain, Browser.now() - time);
        time = Browser.now();
        if ((this.depthTextureMode & DepthTextureMode.Depth) != 0) {
            this._renderDepthPass(context);
        }
        if ((this.depthTextureMode & DepthTextureMode.DepthNormals) != 0) {
            this._renderDepthNormalPass(context);
        }
        LayaGL.statAgent.recordTimeData(StatElement.T_DepthPass, Browser.now() - time);
        this._viewPort.cloneTo(WebGLForwardAddClusterRP._context3DViewPortCatch);
        this._scissor.cloneTo(WebGLForwardAddClusterRP._contextScissorPortCatch);
        time = Browser.now();
        this._mainPass(context);
        LayaGL.statAgent.recordTimeData(StatElement.T_3DMainPass, Browser.now() - time);
        this.opaqueList._batch.recoverData();
    }
    _renderDepthPass(context) {
        context.pipelineMode = this.depthPipelineMode;
        var viewport = this._viewPort;
        var shadervalue = context.sceneData;
        shadervalue.addDefine(DepthPass.DEPTHPASS);
        shadervalue.setVector(DepthPass.DEFINE_SHADOW_BIAS, Vector4.ZERO);
        Viewport.TEMP.set(viewport.x, viewport.y, viewport.width, viewport.height);
        Vector4.TEMP.setValue(viewport.x, viewport.y, viewport.width, viewport.height);
        context.setViewPort(Viewport.TEMP);
        context.setScissor(Vector4.TEMP);
        context.setRenderTarget(this.depthTarget, RenderClearFlag.Depth);
        context.setClearData(RenderClearFlag.Depth, Color.BLACK, 1, 0);
        this.opaqueList.renderQueue(context);
        LayaGL.statAgent.recordCTData(StatElement.CT_DepthCastDrawCall, this.opaqueList.elements.length);
        var far = this.camera.farplane;
        var near = this.camera.nearplane;
        this._zBufferParams.setValue(1.0 - far / near, far / near, (near - far) / (near * far), 1 / near);
        context.cameraData.setVector(DepthPass.DEFINE_SHADOW_BIAS, DepthPass.SHADOW_BIAS);
        context.cameraData.setVector(DepthPass.DEPTHZBUFFERPARAMS, this._zBufferParams);
        shadervalue.removeDefine(DepthPass.DEPTHPASS);
    }
    _transparentListRender(context) {
        this.transparent.renderQueue(context);
    }
    _opaqueListRender(context) {
        this.opaqueList.renderQueue(context);
    }
    _renderDepthNormalPass(context) {
        context.pipelineMode = this.depthNormalPipelineMode;
        var viewport = this._viewPort;
        Viewport.TEMP.set(viewport.x, viewport.y, viewport.width, viewport.height);
        Vector4.TEMP.setValue(viewport.x, viewport.y, viewport.width, viewport.height);
        context.setViewPort(Viewport.TEMP);
        context.setScissor(Vector4.TEMP);
        context.setClearData(RenderClearFlag.Color | RenderClearFlag.Depth, this._defaultNormalDepthColor, 1, 0);
        context.setRenderTarget(this.depthNormalTarget, RenderClearFlag.Color | RenderClearFlag.Depth);
        this.opaqueList.renderQueue(context);
        LayaGL.statAgent.recordCTData(StatElement.CT_DepthCastDrawCall, this.opaqueList.elements.length);
    }
    opaqueTexturePass(context) {
        let commanbuffer = this.blitOpaqueBuffer;
        commanbuffer._apply(false);
        context.runCMDList(commanbuffer._renderCMDs);
    }
    _mainPass(context) {
        context.pipelineMode = this.pipelineMode;
        this._rendercmd(this.beforeForwardCmds, context);
        this._recoverRenderContext3D(context);
        context.setClearData(this.clearFlag, this.clearColor, 1, 0);
        var time = Browser.now();
        this.enableOpaque && this._opaqueListRender(context);
        LayaGL.statAgent.recordTimeData(StatElement.T_3DMainPass_Opaque, Browser.now() - time);
        this._rendercmd(this.beforeSkyboxCmds, context);
        if (this.skyRenderNode) {
            let skyRenderNode = this.skyRenderNode;
            let skyRenderElement = skyRenderNode.renderelements[0];
            context.drawRenderElementOne(skyRenderElement);
        }
        if (this.enableOpaque) {
            this.opaqueTexturePass(context);
        }
        this._rendercmd(this.beforeTransparentCmds, context);
        this._recoverRenderContext3D(context);
        time = Browser.now();
        this.transparent && this._transparentListRender(context);
        LayaGL.statAgent.recordTimeData(StatElement.T_3DMainPass_Trans, Browser.now() - time);
    }
    _rendercmd(cmds, context) {
        if (!cmds || cmds.length == 0)
            return;
        cmds.forEach(function (value) {
            context.runCMDList(value._renderCMDs);
        });
    }
    _recoverRenderContext3D(context) {
        const cacheViewPor = WebGLForwardAddClusterRP._context3DViewPortCatch;
        const cacheScissor = WebGLForwardAddClusterRP._contextScissorPortCatch;
        context.setViewPort(cacheViewPor);
        context.setScissor(cacheScissor);
        context.setRenderTarget(this.destTarget, RenderClearFlag.Nothing);
    }
    destroy() {
        this.cameraCullInfo = null;
        this.beforeForwardCmds = null;
        this.beforeSkyboxCmds = null;
        this.beforeTransparentCmds = null;
        this.blitOpaqueBuffer.clear();
        this.blitOpaqueBuffer = null;
        this.opaqueList.destroy();
        this.transparent.destroy();
    }
}
WebGLForwardAddClusterRP._context3DViewPortCatch = new Viewport(0, 0, 0, 0);
WebGLForwardAddClusterRP._contextScissorPortCatch = new Vector4(0, 0, 0, 0);
